import { DownloadOutlined } from "@ant-design/icons"
import { App, Button, Checkbox, Typography } from "antd"
import { useState } from "react"

import Flex from "#blocks/Flex"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  type ClassroomClass,
  downloadClassroomCard,
  stayingDetail,
} from "#utils/classroomPromotions"

const { Text } = Typography

const ClassroomPromotions = ({
  classes,
  semesterLabel,
  nextSemesterLabel,
}: {
  classes: ClassroomClass[]
  semesterLabel: string
  nextSemesterLabel: string
}) => {
  const theme = useThemeContext()
  const { message } = App.useApp()
  const [offCard, setOffCard] = useState<string[]>([])
  const [detail, setDetail] = useState(false)
  const included = classes.filter(
    (classObj) => classObj.ready && !offCard.includes(classObj.classId)
  )
  const detailRows = included.flatMap((classObj) =>
    classObj.staying
      .map((student) => ({
        key: `${classObj.classId}-${student.studentId}`,
        name: student.name,
        className: classObj.name,
        text: stayingDetail(student),
      }))
      .filter((row) => row.text)
  )

  const download = async () => {
    try {
      const blob = await downloadClassroomCard(
        included,
        detail,
        semesterLabel,
        nextSemesterLabel
      )
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = "classroom-promotions.png"
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      void message.error(
        error instanceof Error ? error.message : "Could not save the card"
      )
    }
  }

  return (
    <Flex gap={24} style={{ alignItems: "flex-start", width: "100%" }}>
      <Flex col gap={12} style={{ flex: 1, minWidth: 0 }}>
        <Flex style={{ alignItems: "center", justifyContent: "space-between" }}>
          <Text type="secondary">
            A class goes on the card once every student is approved, printed, or
            marked no eval.
          </Text>
          <Button
            size="small"
            type={detail ? "primary" : "default"}
            onClick={() => setDetail((open) => !open)}
          >
            {detail ? "Hide detail" : "Show detail"}
          </Button>
        </Flex>
        {classes.map((classObj) => (
          <Flex
            key={classObj.classId}
            col
            gap={4}
            style={{
              border: `1px solid ${theme.gray[2]}`,
              borderRadius: 8,
              padding: 12,
              opacity: classObj.ready ? 1 : 0.72,
            }}
          >
            <Flex gap={8} style={{ alignItems: "center" }}>
              <Text strong>{classObj.name}</Text>
              <Text type="secondary">{classObj.startTime}</Text>
              <Text
                style={{ color: classObj.ready ? theme.success : theme.error }}
              >
                {classObj.ready ? "approved" : "not approved yet"}
              </Text>
              <Checkbox
                style={{ marginLeft: "auto" }}
                checked={classObj.ready && !offCard.includes(classObj.classId)}
                disabled={!classObj.ready}
                onChange={(event) => {
                  setOffCard((ids) =>
                    event.target.checked
                      ? ids.filter((id) => id !== classObj.classId)
                      : [...ids, classObj.classId]
                  )
                }}
              >
                On the card
              </Checkbox>
            </Flex>
            {classObj.ready ? null : (
              <Text type="secondary">
                Approve this class on the Evaluations tab and it becomes
                available here.
              </Text>
            )}
          </Flex>
        ))}
      </Flex>

      <Flex col gap={8} style={{ width: 390, flex: "0 0 390px" }}>
        <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
          <Text type="secondary">The card</Text>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            disabled={!included.length}
            onClick={() => {
              void download()
            }}
          >
            PNG
          </Button>
        </Flex>
        <Flex
          col
          style={{
            width: 390,
            border: `1px solid ${theme.gray[2]}`,
            borderRadius: 16,
            overflow: "hidden",
            background: theme.white,
          }}
        >
          <Flex
            col
            gap={4}
            style={{ background: theme.mainColor, padding: "16px 18px" }}
          >
            <Text style={{ letterSpacing: 1, fontSize: 11 }}>HONGYUN ART</Text>
            <Text strong style={{ fontSize: 22 }}>
              Who leveled up
            </Text>
            <Text>{semesterLabel}</Text>
          </Flex>
          {included.length ? (
            <Flex col gap={16} style={{ padding: 16 }}>
              {included.map((classObj) => (
                <Flex key={classObj.classId} col gap={8}>
                  <Text strong>
                    {classObj.name}{" "}
                    <Text type="secondary">{classObj.startTime}</Text>
                  </Text>
                  {classObj.promoted.map((student) => (
                    <Flex key={student.studentId} col gap={0}>
                      <Text strong>{student.name}</Text>
                      <Text>
                        {student.fromLabel} → {student.toLabel}
                      </Text>
                    </Flex>
                  ))}
                  {classObj.staying.length ? (
                    <Flex col gap={4}>
                      <Text type="secondary" strong>
                        No level up
                      </Text>
                      {classObj.staying.map((student) => (
                        <Text key={student.studentId} type="secondary">
                          {student.name} · stays at {student.fromLabel}
                        </Text>
                      ))}
                    </Flex>
                  ) : null}
                </Flex>
              ))}
              {detail && detailRows.length ? (
                <Flex
                  col
                  gap={8}
                  style={{
                    borderTop: `1px solid ${theme.gray[2]}`,
                    paddingTop: 12,
                  }}
                >
                  <Text strong>No level up — the detail</Text>
                  <Text type="secondary">
                    Staff only. These notes are not for parents.
                  </Text>
                  {detailRows.map((row) => (
                    <Flex key={row.key} col gap={0}>
                      <Text strong>
                        {row.name}{" "}
                        <Text type="secondary">· {row.className}</Text>
                      </Text>
                      <Text>{row.text}</Text>
                    </Flex>
                  ))}
                </Flex>
              ) : null}
              <Text type="secondary">
                {detail
                  ? "Detail view — includes teacher notes. "
                  : "Summary view — no notes. "}
                {nextSemesterLabel}
              </Text>
            </Flex>
          ) : (
            <Text type="secondary" style={{ padding: 16 }}>
              Nothing approved yet. Approve a class on the Evaluations tab and
              it appears here.
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default ClassroomPromotions
